/* eslint react/no-danger:0 */
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import classNames from 'classnames';
import './FormInput.less';

const DECIMAL_EXISTS = /.*\..*/;
const COMMA_EXISTS = /.*,.*/;
const KEY_CODES = {
    SPACE: 31,
    COMMA: 44,
    HYPHEN: 45,
    PERIOD: 46,
    ZERO: 48,
    NINE: 57,
    DELETE: 127
};

/**
 * FormInput Component
**/

class FormInput extends PureComponent {
    static propTypes = {
        /** The Input's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** The Input's label, required for a11y */
        label: PropTypes.node.isRequired,
        /** The actual content of the input */
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** Input's type */
        type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel']),
        /** Want to pass a CSS class to the input? This is your property. */
        inputClassName: PropTypes.string,
        /** Will apply both .is-readonly wrapper classname AND readonly prop to input. Stylistically will remove border and focus state. Use `readonly` prop only to pass attribute to input and retain border (necessary for date pickers) */
        isReadOnly: PropTypes.bool,
        /** For form controls with no visible label, set this to true. Make sure to still add a value to the label property. For screen readers. */
        srOnlyLabel: PropTypes.bool,
        /** Add content to the left of the text. This can be a string, element, or component */
        leftContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** Add content to the right side of the input box. This can be a string, element, or component */
        rightContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** If you are sticking content into the input that needs to accept mouse interactions, set this to true. */
        contentClickable: PropTypes.bool,
        /** When populated with a string, this will display a help message beneath the input */
        helpText: PropTypes.node,
        /** When populated with a string, this will turn the border red and display a validation error beneath the input. */
        errorMsg: PropTypes.node,
        /** Flag to make helpText and errorMsg use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** Called when the value property changes */
        onChange: PropTypes.func
    };

    static defaultProps = {
        srOnlyLabel: false,
        contentClickable: false,
        type: 'text',
        inputClassName: ''
    };

    static displayName = 'FormInput';

    constructor(props) {
        super(props);
        this.state = {
            isAutoFilled: false,
            isDirty: false
        };
    }

    componentDidMount() {
        if (this.input) {
            this.input.addEventListener('webkitAnimationStart', this.onAnimationStart);
            this.input.addEventListener('animationstart', this.onAnimationStart);
        }
    }

    componentWillUnmount() {
        this.input.removeEventListener('webkitAnimationStart', this.onAnimationStart);
        this.input.removeEventListener('animationstart', this.onAnimationStart);
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.isDirty !== !!nextProps.value && !this.state.isAutoFilled) {
            this.setState({isDirty: !!nextProps.value});
        }
    }

    onAnimationStart = ({target, animationName}) => { // eslint-disable-line no-unused-vars
        switch (animationName) {
            case 'onAutoFillCancel':
                this.setState({
                    isAutoFilled: false
                });
                return false;
            case 'onAutoFillStart':
            default:
                this.setState({
                    isAutoFilled: true
                });
                return true;
        }
    }

    /** Can be called exernally to force focus */
    focus = () => {
        this.input.focus();
    }

    handleChange = (event) => {
        if (this.state.isDirty !== !!event.target.value) {
            this.setState({isDirty: !!event.target.value});
        }

        if (this.props.onChange) {
            this.props.onChange(event);
        }
    }

    // eslint-disable-next-line complexity
    handleKeyPressCheckNum = (event) => {
        let rtn = true;

        // Numbers, anything when the ctrl or meta (cmd on mac)
        // key has been pressed and delete are allowed.
        if ((event.charCode >= KEY_CODES.ZERO && event.charCode <= KEY_CODES.NINE) ||
            (event.ctrlKey || event.metaKey || event.charCode === KEY_CODES.DELETE)) {
            rtn = true;
        } else if ((event.charCode === KEY_CODES.HYPHEN && !this.props.value) ||
            (event.charCode === KEY_CODES.PERIOD && !DECIMAL_EXISTS.test(this.props.value)) ||
            (event.charCode === KEY_CODES.COMMA && !COMMA_EXISTS.test(this.props.value))) {
            // Leading minus is allowed for negative numbers. One decimal point
            // also allowed. Some locales use period some use comma for decimal.
            // Forcing the state to dirty because onChange will not fire for a
            // leading minus or leading decimal since the value is not valid
            // yet.
            this.setState({isDirty: true});
            rtn = true;
        } else if (event.charCode >= KEY_CODES.SPACE) {
            // Any other non ctrl/meta enabled keys are ignored.
            event.preventDefault();
            rtn = false;
        }

        // Invoke consumer onKeyPress handler if it exists.
        if (this.props.onKeyPress) {
            this.props.onKeyPress(event);
        }

        return rtn;
    }

    // eslint-disable-next-line complexity
    render() {
        // Extracting onKeyPress and onChange so they aren't included in ...rest
        // eslint-disable-next-line no-unused-vars
        const {label, leftContent, rightContent, contentClickable, className, helpText, inverse, isReadOnly, errorMsg, srOnlyLabel, inputClassName, onKeyPress, onChange, readonly, ...rest} = this.props;
        const notEmpty = (this.state.isDirty || this.state.isAutoFilled || !!this.props.value || (this.props.type === 'number' && this.props.value === 0) || !!this.props.defaultValue);

        const parentClasses = classNames(className, {
            'form-group': true,
            'floating-label': !srOnlyLabel,
            'not-empty': !!notEmpty,
            'empty': !notEmpty,
            'has-icon': !!leftContent,
            'has-feedback': !!rightContent,
            'has-error': !!errorMsg,
            'is-readonly': !!isReadOnly
        });

        const labelClasses = classNames({
            'has-icon': !!leftContent,
            'sr-only': srOnlyLabel
        });

        const inputClasses = classNames('form-control', inputClassName);

        const message = errorMsg || helpText;
        const messageId = `${this.props.id}__help`;

        let inputProps = {
            className: inputClasses,
            'aria-invalid': !!errorMsg,
            ref: (c) => (this.input = c),
            onChange: this.handleChange,
            onKeyPress: this.props.type === 'number' ? this.handleKeyPressCheckNum : null,
            readOnly: (readonly || isReadOnly),
            ...(typeof label === 'string' && {'aria-label': label, 'placeholder': label}),
            ...(message && {'aria-describedby': messageId}),
            ...rest
        };
        let helpProps = {
            className: classNames('help-block', {
                'text-white': inverse,
            }),
            ...(errorMsg && {role: 'alert', 'aria-live': 'polite'})
        };

        return (
            <div className={parentClasses}>
                <input {...inputProps} />

                <label className={labelClasses} htmlFor={this.props.id} >{label}</label>

                {leftContent &&
                    <span className={classNames('form-control-icon', {'is-clickable': contentClickable})}>{leftContent}</span>
                }

                {rightContent &&
                    <span className={classNames('form-control-feedback', {'is-clickable': contentClickable})}>{rightContent}</span>
                }

                {message && typeof message === 'string' &&
                    <span {...helpProps} id={messageId} dangerouslySetInnerHTML={{__html: message}}/>
                }

                {message && typeof message !== 'string' &&
                    <div {...helpProps} id={messageId}>
                        {message}
                    </div>
                }
            </div>
        );
    }
}

export default FormInput;
